// @intent: Versioned shared contracts for ТЗ Week 7-8 AI Generate runs and phase payloads
// @flow: Generate API/workflow/UI import these schemas to validate run requests, status payloads, SSE events, and persisted phase outputs
// @stale: When AI Generate phase contracts move to v2, add v2 schemas and keep v1 parsers for persisted runs

import { z } from 'zod';

export const GenerationPhaseSchema = z.enum(['brief', 'concept', 'design', 'assets', 'code', 'verify', 'iterate', 'deliver']);
export type GenerationPhase = z.infer<typeof GenerationPhaseSchema>;

export const GenerationRunStatusSchema = z.enum(['pending', 'running', 'succeeded', 'failed', 'cancelled']);
export type GenerationRunStatus = z.infer<typeof GenerationRunStatusSchema>;

export const GenerationLifecycleStateV1Schema = z.enum([
  'created',
  'draft_created',
  'brief_ready',
  'concept_ready',
  'designdoc_ready',
  'assets_ready',
  'code_ready',
  'build_passed',
  'runtime_verified',
  'visual_verified',
  'compliance_verified',
  'delivered_ready',
  'failed',
]);
export type GenerationLifecycleStateV1 = z.infer<typeof GenerationLifecycleStateV1Schema>;

export const GenerationReadinessV1Schema = z.enum(['incomplete', 'building', 'ready', 'failed']);
export type GenerationReadinessV1 = z.infer<typeof GenerationReadinessV1Schema>;

export const GenerationArtifactKindV1Schema = z.enum([
  'prompt_normalization',
  'brief',
  'creative_brief',
  'concept_candidates',
  'design_doc',
  'art_bible',
  'asset_manifest',
  'match3_generation_examples',
  'match3_level',
  'match3_skin_pack',
  'code_bundle',
  'build_report',
  'runtime_report',
  'visual_report',
  'compliance_report',
  'repair_attempt',
  'cost_unknown',
  'delivery_manifest',
  'failure_report',
  'visual_mechanic_report',
]);
export type GenerationArtifactKindV1 = z.infer<typeof GenerationArtifactKindV1Schema>;

export const GenerationRunArtifactSummaryV1Schema = z.strictObject({
  id: z.uuid(),
  kind: GenerationArtifactKindV1Schema,
  phase: GenerationPhaseSchema.optional(),
  attemptIdx: z.number().int().nonnegative(),
  createdAt: z.iso.datetime(),
  /** @intent Opt-in raw artifact payload (DesignDoc/ArtBible/AssetPlan/etc.) for turn review; omitted by default. */
  payload: z.unknown().optional(),
});
export type GenerationRunArtifactSummaryV1 = z.infer<typeof GenerationRunArtifactSummaryV1Schema>;

export const GenerationFailureReportV1Schema = z.strictObject({
  version: z.literal('v1'),
  errorKind: z.string().trim().min(1),
  message: z.string().trim().min(1),
  projectId: z.uuid().optional(),
  phase: GenerationPhaseSchema.optional(),
  blockers: z.array(z.string().trim().min(1)).default([]),
  verifierErrors: z.array(z.string().trim().min(1)).default([]),
  diagnostics: z.strictObject({
    probeInstrumentation: z.strictObject({
      status: z.enum(['missing-trace', 'missing-provider', 'invalid-provider', 'unknown']),
      message: z.string().trim().min(1),
      errors: z.array(z.string().trim().min(1)).optional(),
    }).optional(),
  }).optional(),
  retryable: z.boolean().default(false),
});
export type GenerationFailureReportV1 = z.infer<typeof GenerationFailureReportV1Schema>;

export const PromptDetectedLanguageSchema = z.enum(['ru', 'en', 'mixed', 'unknown']);
export type PromptDetectedLanguage = z.infer<typeof PromptDetectedLanguageSchema>;

export const PromptInterpretationRiskSchema = z.enum(['low', 'medium', 'high']);
export type PromptInterpretationRisk = z.infer<typeof PromptInterpretationRiskSchema>;

export const PromptFirstGestureSchema = z.enum(['tap', 'swipe', 'drag', 'hold', 'aim-release']);
export type PromptFirstGesture = z.infer<typeof PromptFirstGestureSchema>;

export const NormalizedPlayableBriefV1Schema = z.strictObject({
  genre: z.string().trim().min(1),
  subgenre: z.string().trim().min(1).optional(),
  firstGesture: PromptFirstGestureSchema,
  mechanics: z.array(z.string().trim().min(1)).min(1),
  entities: z.array(z.string().trim().min(1)).default([]),
  visualMotifs: z.array(z.string().trim().min(1)).default([]),
  sceneIntent: z.string().trim().min(1),
  gameplayGoal: z.string().trim().min(1),
  forbiddenMisreadings: z.array(z.string().trim().min(1)).min(1),
  // @intent A2: how many sequential gameplay levels the prompt asks for (e.g. "maze with 3 levels"). Optional;
  //         absent or 1 == a single-level playable (unchanged path). Clamped 1..5 by the normalizer.
  levelCount: z.number().int().min(1).max(5).optional(),
});
export type NormalizedPlayableBriefV1 = z.infer<typeof NormalizedPlayableBriefV1Schema>;

export const PromptNormalizerOutputV1Schema = z.strictObject({
  version: z.literal('v1'),
  rawPrompt: z.string().trim().min(1),
  detectedLanguage: PromptDetectedLanguageSchema,
  englishIntent: z.string().trim().min(1),
  interpretationRisk: PromptInterpretationRiskSchema,
  assumptions: z.array(z.string().trim().min(1)).default([]),
  normalizedBrief: NormalizedPlayableBriefV1Schema,
});
export type PromptNormalizerOutputV1 = z.infer<typeof PromptNormalizerOutputV1Schema>;

export const AiGenerateTargetPlatformSchema = z.enum(['preview', 'unity']);
export type AiGenerateTargetPlatform = z.infer<typeof AiGenerateTargetPlatformSchema>;

export const AiGenerateStartRequestSchema = z.strictObject({
  prompt: z.string().trim().min(8).max(4000),
  targetPlatform: AiGenerateTargetPlatformSchema.default('preview'),
  templateId: z.uuid().optional(),
});
export type AiGenerateStartRequest = z.infer<typeof AiGenerateStartRequestSchema>;

export const BriefDocV1Schema = z.strictObject({
  version: z.literal('v1'),
  product: z.string().min(1),
  audience: z.string().min(1),
  goal: z.string().min(1),
  constraints: z.array(z.string().min(1)).default([]),
  style: z.array(z.string().min(1)).default([]),
  intent: PromptNormalizerOutputV1Schema.optional(),
});
export type BriefDocV1 = z.infer<typeof BriefDocV1Schema>;

export const CreativeBriefV1Schema = z.strictObject({
  version: z.literal('v1'),
  sourceIntent: PromptNormalizerOutputV1Schema,
  product: z.string().trim().min(1),
  audience: z.string().trim().min(1),
  campaignGoal: z.string().trim().min(1),
  language: PromptDetectedLanguageSchema,
  targetProfile: AiGenerateTargetPlatformSchema,
  durationSec: z.number().int().positive(),
  oneSentenceFantasy: z.string().trim().min(1),
  promiseToUser: z.string().trim().min(1),
  assumptions: z.array(z.string().trim().min(1)).default([]),
  constraints: z.array(z.string().trim().min(1)).default([]),
  styleHints: z.array(z.string().trim().min(1)).default([]),
  forbiddenMisreadings: z.array(z.string().trim().min(1)).min(1),
});
export type CreativeBriefV1 = z.infer<typeof CreativeBriefV1Schema>;

export const AssetIntentSchema = z.enum(['sprite', 'icon', 'ui', 'scene', 'background']);
export type AssetIntent = z.infer<typeof AssetIntentSchema>;

export const AssetAspectRatioSchema = z.enum(['1:1', '3:4', '4:3', '9:16', '16:9']);
export type AssetAspectRatio = z.infer<typeof AssetAspectRatioSchema>;

export const AssetPlanV1Schema = z.strictObject({
  id: z.string().min(1),
  intent: AssetIntentSchema,
  prompt: z.string().min(1),
  aspectRatio: AssetAspectRatioSchema.optional(),
  required: z.boolean().optional(),
});
export type AssetPlanV1 = z.infer<typeof AssetPlanV1Schema>;

export const ConceptCandidateV1Schema = z.strictObject({
  id: z.string().trim().min(1),
  title: z.string().trim().min(1),
  oneSentenceFantasy: z.string().trim().min(1),
  hook: z.string().trim().min(1),
  firstGesture: PromptFirstGestureSchema,
  payoff: z.string().trim().min(1),
  installIntentReason: z.string().trim().min(1),
  risks: z.array(z.string().trim().min(1)).default([]),
  verificationTargets: z.array(z.string().trim().min(1)).min(1),
  hookScore: z.number().min(0).max(10),
  gestureClarityScore: z.number().min(0).max(10),
  visualPotentialScore: z.number().min(0).max(10),
  gameplayLoopScore: z.number().min(0).max(10),
  productionFeasibilityScore: z.number().min(0).max(10),
  verificationFitScore: z.number().min(0).max(10),
  creativeScore: z.number().min(0).max(10),
});
export type ConceptCandidateV1 = z.infer<typeof ConceptCandidateV1Schema>;

export const ConceptCandidatesV1Schema = z.strictObject({
  version: z.literal('v1'),
  selectedId: z.string().trim().min(1),
  candidates: z.array(ConceptCandidateV1Schema).min(3).max(7),
}).superRefine((value, ctx) => {
  const selected = value.candidates.find((candidate) => candidate.id === value.selectedId);
  if (!selected) {
    ctx.addIssue({
      code: 'custom',
      path: ['selectedId'],
      message: 'selectedId must match one concept candidate id',
    });
    return;
  }

  const highestCreativeScore = Math.max(...value.candidates.map((candidate) => candidate.creativeScore));
  if (selected.creativeScore !== highestCreativeScore) {
    ctx.addIssue({
      code: 'custom',
      path: ['selectedId'],
      message: 'selectedId must reference the highest creativeScore candidate',
    });
  }
});
export type ConceptCandidatesV1 = z.infer<typeof ConceptCandidatesV1Schema>;

export const PlayableArcBeatV1Schema = z.strictObject({
  name: z.enum(['hook', 'tutorial', 'gameplay', 'payoff', 'endcard']),
  summary: z.string().trim().min(1),
  goal: z.string().trim().min(1),
});
export type PlayableArcBeatV1 = z.infer<typeof PlayableArcBeatV1Schema>;

export const StateMachineStateV1Schema = z.strictObject({
  id: z.string().trim().min(1),
  label: z.string().trim().min(1),
  isTerminal: z.boolean().default(false),
});
export type StateMachineStateV1 = z.infer<typeof StateMachineStateV1Schema>;

export const StateMachineTransitionV1Schema = z.strictObject({
  fromState: z.string().trim().min(1),
  toState: z.string().trim().min(1),
  trigger: z.string().trim().min(1),
  note: z.string().trim().min(1).optional(),
});
export type StateMachineTransitionV1 = z.infer<typeof StateMachineTransitionV1Schema>;

export const LayoutSafeAreasV1Schema = z.strictObject({
  top: z.number().int().nonnegative(),
  right: z.number().int().nonnegative(),
  bottom: z.number().int().nonnegative(),
  left: z.number().int().nonnegative(),
});
export type LayoutSafeAreasV1 = z.infer<typeof LayoutSafeAreasV1Schema>;

export const DesignSceneV1Schema = z.strictObject({
  id: z.string().min(1),
  description: z.string().min(1),
});
export type DesignSceneV1 = z.infer<typeof DesignSceneV1Schema>;

export const DesignDocV1Schema = z.strictObject({
  version: z.literal('v1'),
  title: z.string().min(1),
  viewport: z.strictObject({
    width: z.number().int().positive(),
    height: z.number().int().positive(),
  }),
  mechanics: z.array(z.string().min(1)).min(1),
  assets: z.array(AssetPlanV1Schema).default([]),
  scenes: z.array(DesignSceneV1Schema).min(1),
  testCriteria: z.array(z.string().min(1)).min(1),
  brief: BriefDocV1Schema,
});
export type DesignDocV1 = z.infer<typeof DesignDocV1Schema>;

// @intent Shared coordinate type for topology grids
const PointV1Schema = z.strictObject({ x: z.number().int().nonnegative(), y: z.number().int().nonnegative() });

// @intent One solvable maze level (grid + collision rules + solution path).
// @flow Shared per-level shape: the primary maze topology IS a level; A2 multi-level mazes carry sibling levels.
// @stale 2026-05-28 — keep field set identical to a single-level maze (no `levels` recursion here).
const MazeLevelV1Schema = z.strictObject({
  family: z.literal('maze'),
  topologyId: z.string().trim().min(1),
  topologyHash: z.string().trim().min(1),
  grid: z.strictObject({ cols: z.number().int().positive(), rows: z.number().int().positive() }),
  legend: z.record(z.string(), z.number().int()),
  cells: z.array(z.array(z.number().int())).min(1),
  playerStart: PointV1Schema,
  goal: PointV1Schema,
  solutionPath: z.array(PointV1Schema).min(1),
  collisionRules: z.record(z.string(), z.unknown()),
  allowedGestures: z.array(z.string().trim().min(1)).min(1),
  seed: z.number().int(),
  difficulty: z.enum(['easy', 'medium', 'hard']),
});
export type MazeLevelV1 = z.infer<typeof MazeLevelV1Schema>;

// @intent Maze topology — solvable grid-based maze with collision rules and solution path (A2: optional multi-level arc).
// @flow LLM/deterministic pipeline fills this when mechanic family is 'maze'; runtime uses solutionPath to verify.
//        The top-level fields ALWAYS describe the PRIMARY (level 0) maze so single-level correspondence checks
//        (F9 verifier: topologyId/cells/solutionPath) keep working unchanged. When the prompt asks for N>1 levels,
//        `levels` carries ALL N levels (levels[0] === the primary level), each a distinct solvable maze; codegen
//        advances through them and reaches the end card after the final level.
// @stale 2026-05-28 — keep top-level == primary level; `levels` is additive (non-recursive, one nesting depth only).
export const MazeTopologyV1Schema = MazeLevelV1Schema.extend({
  levels: z.array(MazeLevelV1Schema).min(1).optional(),
});
export type MazeTopologyV1 = z.infer<typeof MazeTopologyV1Schema>;

// @intent Runner topology — lane-based infinite runner with obstacles
// @flow LLM fills this when mechanic family is 'runner'; runtime spawns obstacles per lane
// @stale 2026-05-28
export const RunnerTopologyV1Schema = z.strictObject({
  family: z.literal('runner'), topologyId: z.string().trim().min(1), topologyHash: z.string().trim().min(1),
  lanes: z.number().int().positive(), laneGeometry: z.record(z.string(), z.unknown()),
  obstacles: z.array(z.strictObject({ lane: z.number().int().nonnegative() }).catchall(z.unknown())),
  allowedGestures: z.array(z.string().trim().min(1)).min(1), seed: z.number().int(),
});
export type RunnerTopologyV1 = z.infer<typeof RunnerTopologyV1Schema>;

// @intent Puzzle topology — board-based puzzle with pieces, win/fail conditions
// @flow LLM fills this when mechanic family is 'puzzle'; runtime validates win/fail conditions
// @stale 2026-05-28
export const PuzzleTopologyV1Schema = z.strictObject({
  family: z.literal('puzzle'), topologyId: z.string().trim().min(1), topologyHash: z.string().trim().min(1),
  board: z.record(z.string(), z.unknown()), pieces: z.array(z.string().trim().min(1)).min(1),
  winCondition: z.string().trim().min(1), failCondition: z.string().trim().min(1),
  allowedGestures: z.array(z.string().trim().min(1)).min(1), seed: z.number().int(),
});
export type PuzzleTopologyV1 = z.infer<typeof PuzzleTopologyV1Schema>;

// @intent Match topology — swap-to-match-3 tile board with a deterministic, always-playable initial grid.
// @flow LLM/deterministic pipeline fills this when mechanic family is 'match'; runtime swaps adjacent tiles, detects
//        3-in-a-row matches (horizontal + vertical), clears them, scores, and cascades toward a target. The `board`
//        record carries the flat row-major initial grid (`cells`, tile-type indices) plus a deterministic guaranteed
//        first-move (`firstMove`: the two adjacent cell coordinates whose swap makes a match) so the board is ALWAYS
//        playable yet has NO pre-existing 3-in-a-row (the first move matters).
// @stale 2026-05-29 — keep field set/shape aligned with services/ai-generate match-topology.ts; mirror the puzzle/runner topologies.
export const MatchTopologyV1Schema = z.strictObject({
  family: z.literal('match'), topologyId: z.string().trim().min(1), topologyHash: z.string().trim().min(1),
  rows: z.number().int().positive(), cols: z.number().int().positive(),
  // Number of distinct tile kinds (e.g. 4-6 gems/jewels) — every board cell is an index in [0, tileTypes).
  tileTypes: z.number().int().min(2),
  // Stores the initial grid + the deterministic guaranteed first move (record so the shape can extend per variant).
  board: z.record(z.string(), z.unknown()),
  // The win target — number of points to reach (score-based) OR clears to reach; at least one is present.
  targetScore: z.number().int().positive().optional(),
  targetClears: z.number().int().positive().optional(),
  allowedGestures: z.array(z.string().trim().min(1)).min(1), seed: z.number().int(),
});
export type MatchTopologyV1 = z.infer<typeof MatchTopologyV1Schema>;

// @intent Strongly-typed match3.swap backbone config + runtime board (Match3BackboneSpec §2). These TIGHTEN the loose
//         persisted MatchTopologyV1Schema.board (z.record) at the codegen/verifier BOUNDARY only — the persisted record is
//         deliberately NOT migrated (every reader defaults-on-miss). The config is the LLM-/Tier-1-configurable [L] surface
//         the deterministic generator consumes; the generator emits the Match3Board shape carrying the deterministic firstMove.
// @flow board W×H (+ optional mask) + tile palette + objective + limit + boosters + theme → generateMatchTopology input →
//        runtime board (Match3BoardSchema) with the KNOWN VALID first move the §10 playthrough verifier drives.
// @stale 2026-05-29 — keep in lockstep with services/ai-generate match-topology.ts generateMatchTopology + the §10 verifier hooks.

/** A single board cell coordinate (row + col, row-major). */
export const Match3CellSchema = z.strictObject({
  row: z.number().int().nonnegative(),
  col: z.number().int().nonnegative(),
});
export type Match3Cell = z.infer<typeof Match3CellSchema>;

/** The deterministic guaranteed first move — two orthogonally-adjacent cells whose swap creates a match (the KNOWN VALID MOVE). */
export const Match3FirstMoveSchema = z.strictObject({ a: Match3CellSchema, b: Match3CellSchema });
export type Match3FirstMove = z.infer<typeof Match3FirstMoveSchema>;

/** The win objective — what advancing it means + the target count (+ optional tile/collectible role it tracks). */
export const Match3ObjectiveSchema = z.strictObject({
  type: z.enum(['collectN', 'scoreTarget', 'pvpRace', 'grow']),
  target: z.number().int().positive(),
  role: z.string().trim().min(1).optional(),
});
export type Match3Objective = z.infer<typeof Match3ObjectiveSchema>;

/** The play limit — moves / timer(seconds) / turns / none. discriminatedUnion + strictObject ⇒ no variant may carry a foreign field. */
export const Match3LimitSchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('moves'), value: z.number().int().positive() }),
  z.strictObject({ kind: z.literal('timer'), seconds: z.number().int().positive() }),
  z.strictObject({ kind: z.literal('turns'), value: z.number().int().positive() }),
  z.strictObject({ kind: z.literal('none') }),
]);
export type Match3Limit = z.infer<typeof Match3LimitSchema>;

/** A booster — CONFIG-AXIS ONLY in phase B: persisted + validated but NOT gameplay-active (gameplayActive is literal false). */
export const Match3BoosterSchema = z.strictObject({
  id: z.string().trim().min(1),
  gameplayActive: z.literal(false),
});
export type Match3Booster = z.infer<typeof Match3BoosterSchema>;

/** The runtime board shape — the TIGHTENED view of the loose persisted MatchTopologyV1Schema.board record. */
export const Match3BoardSchema = z
  .strictObject({
    rows: z.number().int().positive(),
    cols: z.number().int().positive(),
    cells: z.array(z.number().int()),
    tileTypes: z.number().int().min(3).max(7),
    firstMove: Match3FirstMoveSchema,
    enabledMask: z.array(z.boolean()).optional(),
    objective: Match3ObjectiveSchema.optional(),
    limit: Match3LimitSchema.optional(),
    boosters: z.array(Match3BoosterSchema).optional(),
  })
  .superRefine((b, ctx) => {
    const expected = b.rows * b.cols;
    if (b.cells.length !== expected) {
      ctx.addIssue({ code: 'custom', path: ['cells'], message: `cells length ${String(b.cells.length)} must equal rows*cols ${String(expected)}` });
    }
    if (b.enabledMask && b.enabledMask.length !== expected) {
      ctx.addIssue({ code: 'custom', path: ['enabledMask'], message: `enabledMask length must equal rows*cols ${String(expected)}` });
    }
    for (const cell of b.cells) {
      if (cell >= b.tileTypes) {
        ctx.addIssue({ code: 'custom', path: ['cells'], message: `cell type ${String(cell)} out of range [0, ${String(b.tileTypes)})` });
        break;
      }
    }
  });
export type Match3Board = z.infer<typeof Match3BoardSchema>;

/** The LLM-/Tier-1-configurable match3.swap backbone config (Match3BackboneSpec §2 [L] fields). */
export const Match3BackboneConfigSchema = z
  .strictObject({
    seed: z.number().int(),
    rows: z.number().int().min(3).max(12).default(8),
    cols: z.number().int().min(3).max(12).default(8),
    tileTypeCount: z.number().int().min(3).max(7),
    tileSkins: z.array(z.string().trim().min(1)),
    objective: Match3ObjectiveSchema,
    limit: Match3LimitSchema,
    boosters: z.array(Match3BoosterSchema).default([]),
    enabledMask: z.array(z.boolean()).optional(),
    theme: z.record(z.string(), z.unknown()).optional(),
    layout: z.record(z.string(), z.unknown()).optional(),
  })
  .superRefine((cfg, ctx) => {
    if (cfg.tileSkins.length !== cfg.tileTypeCount) {
      ctx.addIssue({ code: 'custom', path: ['tileSkins'], message: `tileSkins length ${String(cfg.tileSkins.length)} must equal tileTypeCount ${String(cfg.tileTypeCount)}` });
    }
    if (cfg.enabledMask && cfg.enabledMask.length !== cfg.rows * cfg.cols) {
      ctx.addIssue({ code: 'custom', path: ['enabledMask'], message: `enabledMask length must equal rows*cols` });
    }
  });
export type Match3BackboneConfig = z.infer<typeof Match3BackboneConfigSchema>;

// @intent Shooter topology — lane-based tap-shooter with a deterministic, ALWAYS-winnable enemy-wave layout.
// @flow LLM/deterministic pipeline fills this when mechanic family is 'shooter'; runtime taps a lane to fire a VISIBLE
//        bullet from the player up the tapped lane, destroying the advancing enemy in that lane and scoring. The
//        `enemyWaves` array carries one entry per enemy: its lane (the lane the player taps to shoot it) + its forward
//        `wave` (advance order). The board is ALWAYS winnable: every enemy sits in a valid lane in [0, lanes) so a tap on
//        its lane fires a bullet that destroys it, and `enemiesToClear` never exceeds the enemy count so a finite,
//        reachable target always exists. Lanes (e.g. 3) carry VISIBLE lane dividers; a VISIBLE score HUD tracks progress
//        — the exact judge gaps the shooter genre faulted on (no visible projectile / no visible lane dividers / no HUD).
// @stale 2026-05-29 — keep field set/shape aligned with services/ai-generate shooter-topology.ts; mirror the runner/match topologies.
export const ShooterTopologyV1Schema = z.strictObject({
  family: z.literal('shooter'), topologyId: z.string().trim().min(1), topologyHash: z.string().trim().min(1),
  lanes: z.number().int().positive(), laneGeometry: z.record(z.string(), z.unknown()),
  // One entry per enemy: which lane it advances down (the lane the player taps to shoot it) + its forward wave index.
  enemyWaves: z.array(z.strictObject({ lane: z.number().int().nonnegative() }).catchall(z.unknown())),
  // The win target — number of enemies to clear (always reachable: <= enemyWaves.length); a score target may accompany it.
  enemiesToClear: z.number().int().positive(),
  targetScore: z.number().int().positive().optional(),
  allowedGestures: z.array(z.string().trim().min(1)).min(1), seed: z.number().int(),
});
export type ShooterTopologyV1 = z.infer<typeof ShooterTopologyV1Schema>;

// @intent Tower-defense topology — a fixed enemy PATH, path-side tower SLOTS, and increasing enemy WAVES, with a
//          deterministic, ALWAYS-winnable layout.
// @flow LLM/deterministic pipeline fills this when mechanic family is 'tower'; runtime renders the VISIBLE path tiles
//        (pathCells, the ordered cells enemies walk from spawn → base), lets the player TAP a path-side `towerSlots`
//        cell to PLACE a visible tower there, and the towers auto-shoot the enemies that march along the path each
//        tick; the player SURVIVES increasing `waves` (each entry is that wave's enemy count, strictly increasing).
//        The field is ALWAYS winnable: every path cell has at least one adjacent tower slot (placing towers covers the
//        whole path so every enemy is reachable by a tower's fire), and the wave count is finite + reachable — so a
//        finite "survive all N waves" goal always exists. Tower-defense was the exact 6th genre-true family the gate
//        faulted for being a generic shell (no path / no placeable towers / no waves).
// @stale 2026-05-29 — keep field set/shape aligned with services/ai-generate tower-topology.ts; mirror the shooter/match topologies.
export const TowerTopologyV1Schema = z.strictObject({
  family: z.literal('tower'), topologyId: z.string().trim().min(1), topologyHash: z.string().trim().min(1),
  // Grid the path + slots live on (cells are integer coords in [0, gridCols) x [0, gridRows)).
  gridCols: z.number().int().positive(), gridRows: z.number().int().positive(),
  // The ordered cells the enemies walk: pathCells[0] is the spawn, the last entry is the base/end the enemy reaches.
  pathCells: z.array(z.strictObject({ x: z.number().int().nonnegative(), y: z.number().int().nonnegative() })).min(2),
  // Placeable tower slots adjacent to the path: every path cell has at least one of these next to it (covers the path).
  towerSlots: z.array(z.strictObject({ x: z.number().int().nonnegative(), y: z.number().int().nonnegative() })).min(1),
  // Enemy count per wave (strictly increasing); surviving every wave is the win — a finite, reachable goal.
  waves: z.array(z.strictObject({ enemyCount: z.number().int().positive() }).catchall(z.unknown())).min(1),
  allowedGestures: z.array(z.string().trim().min(1)).min(1), seed: z.number().int(),
});
export type TowerTopologyV1 = z.infer<typeof TowerTopologyV1Schema>;

// @intent Collect topology — a time/target-limited collect-the-items playfield with deterministic, ALWAYS-winnable
//          scattered collectible positions.
// @flow LLM/deterministic pipeline fills this when mechanic family is 'collect'; runtime scatters the `itemPositions`
//        collectibles across the playfield as VISIBLE foreground sprites, lets the player TAP (or drag onto) each item to
//        COLLECT it (the item visibly pops + fades, a VISIBLE score rises), and collecting `targetCollect` items (within
//        the optional `timeLimitMs`) is the win. The field is ALWAYS winnable: `targetCollect` never exceeds `itemCount`
//        and every item sits at a deterministic, in-bounds, tappable position (positions are normalized [0,1] x/y so the
//        layout scales to any viewport). Collect is the simple, reliable analog of the puzzle tap-clear / match backbones —
//        scattered tappable items + a visible score + a target, NOT a board grid.
// @stale 2026-05-29 — keep field set/shape aligned with services/ai-generate collect-topology.ts; mirror the match/puzzle topologies.
export const CollectTopologyV1Schema = z.strictObject({
  family: z.literal('collect'), topologyId: z.string().trim().min(1), topologyHash: z.string().trim().min(1),
  // How many collectible items are scattered in the playfield; every item is independently tappable/collectible.
  itemCount: z.number().int().positive(),
  // Deterministic scattered positions — normalized x/y in [0,1] so the layout is viewport-independent (length === itemCount).
  itemPositions: z.array(z.strictObject({ x: z.number().min(0).max(1), y: z.number().min(0).max(1) })).min(1),
  // The win target — number of items to collect (ALWAYS reachable: 1 <= targetCollect <= itemCount).
  targetCollect: z.number().int().positive(),
  // Optional time limit (ms) the player has to reach targetCollect; absent === collect-the-target with no clock.
  timeLimitMs: z.number().int().positive().optional(),
  allowedGestures: z.array(z.string().trim().min(1)).min(1), seed: z.number().int(),
});
export type CollectTopologyV1 = z.infer<typeof CollectTopologyV1Schema>;

// @intent Discriminated union of all supported mechanic topologies (v1)
// @flow Runtime selects the appropriate topology shape from the DesignDocV2 field
// @stale 2026-05-29
export const MechanicTopologyV1Schema = z.discriminatedUnion('family', [
  MazeTopologyV1Schema, RunnerTopologyV1Schema, PuzzleTopologyV1Schema, MatchTopologyV1Schema, ShooterTopologyV1Schema, TowerTopologyV1Schema, CollectTopologyV1Schema,
]);
export type MechanicTopologyV1 = z.infer<typeof MechanicTopologyV1Schema>;

export const DesignDocV2Schema = z.strictObject({
  version: z.literal('v2'),
  title: z.string().trim().min(1),
  creativeBrief: CreativeBriefV1Schema,
  conceptCandidates: ConceptCandidatesV1Schema,
  selectedConcept: ConceptCandidateV1Schema,
  viewport: z.strictObject({
    width: z.number().int().positive(),
    height: z.number().int().positive(),
  }),
  playableArc: z.strictObject({
    hook: PlayableArcBeatV1Schema,
    tutorial: PlayableArcBeatV1Schema,
    gameplay: PlayableArcBeatV1Schema,
    payoff: PlayableArcBeatV1Schema,
    endcard: PlayableArcBeatV1Schema,
  }),
  stateMachine: z.strictObject({
    states: z.array(StateMachineStateV1Schema).min(2),
    initialStateId: z.string().trim().min(1),
    transitions: z.array(StateMachineTransitionV1Schema).min(1),
    firstInput: PromptFirstGestureSchema,
    successConditions: z.array(z.string().trim().min(1)).min(1),
    failConditions: z.array(z.string().trim().min(1)).default([]),
  }),
  mechanics: z.array(z.string().trim().min(1)).min(1),
  firstGesture: PromptFirstGestureSchema,
  ctaPolicy: z.strictObject({
    text: z.string().trim().min(1),
    placement: z.string().trim().min(1),
    timingMs: z.number().nonnegative(),
    minProgressPct: z.number().min(0).max(100),
  }),
  telemetryEvents: z.array(z.string().trim().min(1)).min(1),
  layout: z.strictObject({
    safeAreas: LayoutSafeAreasV1Schema,
  }),
  capabilityUsage: z.array(z.string().trim().min(1)).min(1),
  assetPlan: z.array(AssetPlanV1Schema).min(1),
  verifierTargets: z.array(z.string().trim().min(1)).min(1),
  testCriteria: z.array(z.string().trim().min(1)).min(1),
  mechanicTopology: MechanicTopologyV1Schema.optional(),
}).superRefine((value, ctx) => {
  if (value.selectedConcept.id !== value.conceptCandidates.selectedId) {
    ctx.addIssue({
      code: 'custom',
      path: ['selectedConcept', 'id'],
      message: 'selectedConcept.id must match conceptCandidates.selectedId',
    });
  }
  if (value.selectedConcept.firstGesture !== value.firstGesture) {
    ctx.addIssue({
      code: 'custom',
      path: ['firstGesture'],
      message: 'firstGesture must match selectedConcept.firstGesture',
    });
  }
  if (value.stateMachine.firstInput !== value.firstGesture) {
    ctx.addIssue({
      code: 'custom',
      path: ['stateMachine', 'firstInput'],
      message: 'stateMachine.firstInput must match firstGesture',
    });
  }

  const stateIds = new Set(value.stateMachine.states.map((state) => state.id));
  if (!stateIds.has(value.stateMachine.initialStateId)) {
    ctx.addIssue({
      code: 'custom',
      path: ['stateMachine', 'initialStateId'],
      message: 'initialStateId must reference an existing state id',
    });
  }
  if (!value.stateMachine.states.some((state) => state.isTerminal)) {
    ctx.addIssue({
      code: 'custom',
      path: ['stateMachine', 'states'],
      message: 'stateMachine must include at least one terminal state',
    });
  }
  if (!value.stateMachine.states.some((state) => !state.isTerminal)) {
    ctx.addIssue({
      code: 'custom',
      path: ['stateMachine', 'states'],
      message: 'stateMachine must include at least one nonterminal state',
    });
  }
  for (const [index, transition] of value.stateMachine.transitions.entries()) {
    if (!stateIds.has(transition.fromState)) {
      ctx.addIssue({
        code: 'custom',
        path: ['stateMachine', 'transitions', index, 'fromState'],
        message: 'transition.fromState must reference an existing state id',
      });
    }
    if (!stateIds.has(transition.toState)) {
      ctx.addIssue({
        code: 'custom',
        path: ['stateMachine', 'transitions', index, 'toState'],
        message: 'transition.toState must reference an existing state id',
      });
    }
  }
});
export type DesignDocV2 = z.infer<typeof DesignDocV2Schema>;

export const ArtBibleV1Schema = z.strictObject({
  version: z.literal('v1'),
  styleName: z.string().trim().min(1),
  composition: z.array(z.string().trim().min(1)).min(1),
  palette: z.array(z.string().trim().min(1)).min(1),
  lighting: z.string().trim().min(1),
  shapeLanguage: z.array(z.string().trim().min(1)).min(1),
  uiTreatment: z.string().trim().min(1),
  negativePrompts: z.array(z.string().trim().min(1)).default([]),
  assetPrompts: z.array(z.strictObject({
    planId: z.string().trim().min(1),
    intent: AssetIntentSchema,
    prompt: z.string().trim().min(1),
    negativePrompt: z.string().trim().min(1),
    styleNotes: z.string().trim().min(1),
  })).default([]),
  cutoutRequirements: z.array(z.string().trim().min(1)).default([]),
  styleConsistencyTargets: z.array(z.string().trim().min(1)).default([]),
});
export type ArtBibleV1 = z.infer<typeof ArtBibleV1Schema>;

export const GeneratedAssetManifestV1Schema = z.strictObject({
  version: z.literal('v1'),
  assets: z.array(z.strictObject({
    planId: z.string().min(1),
    assetId: z.uuid(),
    url: z.string().min(1),
    intent: AssetIntentSchema,
  })).default([]),
});
export type GeneratedAssetManifestV1 = z.infer<typeof GeneratedAssetManifestV1Schema>;

export const CodeBundleV1Schema = z.strictObject({
  version: z.literal('v1'),
  code: z.string().min(1),
  summary: z.string().min(1),
});
export type CodeBundleV1 = z.infer<typeof CodeBundleV1Schema>;

export const VerifyReportV1Schema = z.strictObject({
  version: z.literal('v1'),
  pass: z.boolean(),
  errors: z.array(z.string().min(1)).default([]),
  warnings: z.array(z.string().min(1)).default([]),
  evidence: z.record(z.string(), z.unknown()).default({}),
});
export type VerifyReportV1 = z.infer<typeof VerifyReportV1Schema>;

export const IterationReportV1Schema = z.strictObject({
  version: z.literal('v1'),
  cyclesUsed: z.number().int().nonnegative(),
  finalPass: z.boolean(),
  summary: z.string().min(1),
});
export type IterationReportV1 = z.infer<typeof IterationReportV1Schema>;

export const DeliveryManifestV1Schema = z.strictObject({
  version: z.literal('v1'),
  projectId: z.uuid(),
  commitId: z.uuid(),
  previewExportId: z.uuid(),
  exportId: z.uuid(),
  targetPlatform: AiGenerateTargetPlatformSchema,
  previewUrl: z.string().min(1),
});
export type DeliveryManifestV1 = z.infer<typeof DeliveryManifestV1Schema>;

export const RepairReportV1Schema = z.strictObject({
  version: z.literal('v1'),
  owner: z.enum(['concept_design', 'art_assets', 'runtime_code', 'export_compliance']),
  severity: z.enum(['minor', 'major', 'blocker']),
  phase: GenerationPhaseSchema,
  reasons: z.array(z.string().trim().min(1)).min(1),
  evidence: z.record(z.string(), z.unknown()).default({}),
  repairInstruction: z.string().trim().min(1),
  sourceArtifactKinds: z.array(GenerationArtifactKindV1Schema).min(1),
});
export type RepairReportV1 = z.infer<typeof RepairReportV1Schema>;

export const GenerationPhaseOutputV1Schema = z.discriminatedUnion('phase', [
  z.strictObject({ version: z.literal('v1'), phase: z.literal('brief'), output: z.union([BriefDocV1Schema, CreativeBriefV1Schema]) }),
  z.strictObject({ version: z.literal('v1'), phase: z.literal('concept'), output: ConceptCandidatesV1Schema }),
  z.strictObject({ version: z.literal('v1'), phase: z.literal('design'), output: z.union([DesignDocV1Schema, DesignDocV2Schema]) }),
  z.strictObject({ version: z.literal('v1'), phase: z.literal('assets'), output: GeneratedAssetManifestV1Schema }),
  z.strictObject({ version: z.literal('v1'), phase: z.literal('code'), output: CodeBundleV1Schema }),
  z.strictObject({ version: z.literal('v1'), phase: z.literal('verify'), output: VerifyReportV1Schema }),
  z.strictObject({ version: z.literal('v1'), phase: z.literal('iterate'), output: IterationReportV1Schema }),
  z.strictObject({ version: z.literal('v1'), phase: z.literal('deliver'), output: DeliveryManifestV1Schema }),
]);
export type GenerationPhaseOutputV1 = z.infer<typeof GenerationPhaseOutputV1Schema>;

export const GenerationRunEventV1Schema = z.strictObject({
  version: z.literal('v1'),
  sequence: z.number().int().nonnegative(),
  type: z.enum(['run.started', 'phase.started', 'phase.completed', 'asset.generated', 'asset.failed', 'run.completed', 'run.failed']),
  phase: GenerationPhaseSchema.optional(),
  message: z.string().min(1),
  payload: z.record(z.string(), z.unknown()).default({}),
});
export type GenerationRunEventV1 = z.infer<typeof GenerationRunEventV1Schema>;

export const AiGenerateRunStatusResponseSchema = z.strictObject({
  runId: z.uuid(),
  projectId: z.uuid().optional(),
  exportId: z.uuid().optional(),
  status: GenerationRunStatusSchema,
  lifecycleState: GenerationLifecycleStateV1Schema,
  readiness: GenerationReadinessV1Schema,
  failure: GenerationFailureReportV1Schema.optional(),
  currentPhase: GenerationPhaseSchema.optional(),
  events: z.array(GenerationRunEventV1Schema).default([]),
  artifacts: z.array(GenerationRunArtifactSummaryV1Schema).default([]),
});
export type AiGenerateRunStatusResponse = z.infer<typeof AiGenerateRunStatusResponseSchema>;
